import { z } from "zod";
import { PROFESSIONAL_LEVEL_ENUM } from "./practiceSurvey";
import { PaintMixOutputSchema } from "./mix";
import { PaletteOutputSchema } from "./palette";
import { EventRegistrationOutputSchema } from "./eventRegistration";
import { AddressOutputSchema } from "./address";
import { NewsletterSubscriptionDetail } from "./newsletterSubscription";

export const formatErrors = (zodError: z.ZodError) => zodError.issues.map((error) => `${error.path.join(".")} — ${error.message}`);

export const RegisterInputSchema = z.object({
  firstName: z.string(),
  lastName: z.string(),
  newsletterSubscriptionDetail: NewsletterSubscriptionDetail.optional(),
});

export type RegisterInputType = z.infer<typeof RegisterInputSchema>;

export const preRegistrationInputSchema = RegisterInputSchema.extend({
  email: z.email(),
});

export type PreRegistrationInputType = z.infer<typeof preRegistrationInputSchema>;

export const GenCheckinOutputSchema = z.object({
  checkinType: z.string(),
  checkinDate: z.date(),
});

export type GenCheckinOutputType = z.infer<typeof GenCheckinOutputSchema>;
export const UpdateProfileInputSchema = z
  .object({
    firstName: z.string().optional(),
    lastName: z.string().optional(),
    website: z.string().optional(),

    professionalLevel: z.enum(PROFESSIONAL_LEVEL_ENUM).optional(),
    primaryMedium: z.string().optional(),
    additionalMediums: z.array(z.string()).optional(),

    phoneNumber: z
      .string()
      .optional()
      .refine((val) => !val || /^\d+$/.test(val), {
        message: "Phone number must contain only numbers",
      }),
    phoneNumberCC: z
      .string()
      .optional()
      .refine((val) => !val || /^\d+$/.test(val), {
        message: "Country code must contain only numbers",
      }),
    facebookLink: z.string().optional(),
    linkedInLink: z.string().optional(),
    instagramLink: z.string().optional(),
  })
  .superRefine((data, ctx) => {
    if (data.phoneNumber && !data.phoneNumberCC) {
      ctx.addIssue({
        path: ["phoneNumberCC"],
        message: "Phone number country code is required when phone number is provided",
        code: "custom",
      });
    }
  });
export type UpdateProfileInputType = z.infer<typeof UpdateProfileInputSchema>;

export const PostSubscribedCategoriesInputSchema = z.object({
  newCategories: z.array(z.number()),
});

export type PostSubscribedCategoriesInputType = z.infer<typeof PostSubscribedCategoriesInputSchema>;

export const SubscribedCategoriesSchema = z.object({
  categories: z.array(z.number()),
});
export type SubscribedCategoriesType = z.infer<typeof SubscribedCategoriesSchema>;

export const PostFavoritesInputSchema = z.object({
  newFavorites: z.array(z.string()),
});

export type PostFavoritesInputType = z.infer<typeof PostFavoritesInputSchema>;

export const FavoritesSchema = z.object({
  favorites: z.array(z.string()),
});

export type FavoritesType = z.infer<typeof FavoritesSchema>;

export const BasicUserDataSchema = z.object({
  id: z.string(),
  firstName: z.string().optional(),
  lastName: z.string().optional(),
  email: z.string(),
  website: z.string().optional(),

  phoneNumber: z.string().optional(),
  phoneNumberCC: z.string().optional(),
  instagramLink: z.string().optional(),
  linkedInLink: z.string().optional(),
  facebookLink: z.string().optional(),

  professionalLevel: z.enum(PROFESSIONAL_LEVEL_ENUM).optional(),
  primaryMedium: z.string().optional(),
  additionalMediums: z.array(z.string()).optional(),

  subscribedCategories: z.array(z.number()).optional(),
  favorites: z.array(z.string()).optional(),

  publicId: z.string().optional(),
  publicName: z.string().optional(),
  createdDate: z.iso.datetime(),
  practiceSurveyCompleted: z.iso.datetime().optional(),
});
export type BasicUserDataType = z.infer<typeof BasicUserDataSchema>;

export const UserOutputSchema = BasicUserDataSchema.extend({
  addresses: z.array(AddressOutputSchema),

  mixes: z.array(PaintMixOutputSchema),
  palettes: z.array(PaletteOutputSchema),
  events: z.array(EventRegistrationOutputSchema),
  checkins: z.array(GenCheckinOutputSchema),

  logins: z.array(z.object({ createdDate: z.iso.datetime() })).optional(),
  lastLoginDate: z.iso.datetime().optional(),
}).catchall(z.unknown());

export type UserOutputType = z.infer<typeof UserOutputSchema>;
